"use client"
import { Stack, VStack } from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import { DonutChart } from "@/components/DonutChart"
import MarkdownParser from "@/components/MarkdownParser"
import { type DonutChartBlock as DonutChartBlockTypes } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface DonutChartProps extends ContentBlockTitleNumberProps {
  block: DonutChartBlockTypes
}

const DonutChartBlock: FC<DonutChartProps> = ({ block, titleNumber }) => {
  const { darker_bg, subtitle, title, two_columns_donut } = block
  const chartBlock = { ...block, subtitle: undefined, title: undefined }
  const trimmedTitle = title?.trim()
  const trimmedSubtitle = subtitle?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription = Boolean(trimmedSubtitle)

  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="donut-chart-block internal-padding"
      direction={{
        base: "column",
        lg: two_columns_donut ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_donut ? 6 : 8 }}
      justifyContent="flex-end"
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          flexShrink={0}
          maxWidth={{
            base: "100%",
            lg: two_columns_donut ? "30%" : "100%",
          }}
          minW={0}
          w={{
            base: "100%",
            lg: two_columns_donut ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_donut))}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={trimmedSubtitle}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="center"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        gap={4}
        maxWidth={{
          base: "100%",
          lg: two_columns_donut ? "70%" : "100%",
        }}
        minW={0}
        pl={!hasTitle && two_columns_donut ? { base: 0, lg: 12 } : 0}
        w={{
          base: "100%",
          lg: two_columns_donut ? "70%" : "100%",
        }}
      >
        <DonutChart block={chartBlock} />
      </VStack>
    </Stack>
  )
}

export default DonutChartBlock
